import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider } from "@/lib/i18n";
import Landing from "@/components/Landing";
import WorkList from "@/pages/WorkList";
import WorkDetail from "@/pages/WorkDetail";
import Studio from "@/pages/Studio";
import CaseStudyDraftPreview from "@/pages/studio/CaseStudyDraftPreview";
import SharedPack from "@/pages/SharedPack";
import Journal from "@/pages/Journal";
import JournalDetail from "@/pages/JournalDetail";
import Intake from "@/pages/Intake";
import Kit from "@/pages/Kit";
import StudioOrderEditor from "@/pages/StudioOrderEditor";
import Gallery from "@/pages/Gallery";
import B2B from "@/pages/B2B";
import SalesProposal from "@/pages/SalesProposal";
import DocPrint from "@/pages/studio/DocPrint";
import { ClientLogin, ClientOnboarding, ClientDashboard, ClientEditor, ClientPreview, ClientDomain, ClientAccount, ClientTeam } from "@/pages/client/ClientPortal";
import SeoManager from "@/components/SeoManager";
import IntentPage from "@/pages/IntentPage";
import PublicLanding from "@/pages/PublicLanding";
import Subscribe, { SubscribeSuccess } from "@/pages/Subscribe";
import KitSuccess from "@/pages/KitSuccess";
import BookingSuccess from "@/pages/BookingSuccess";
import PayBalance, { PayBalanceSuccess } from "@/pages/PayBalance";
import MemberDigests, { MemberDigestDetail } from "@/pages/MemberDigests";
import MemberOrders from "@/pages/MemberOrders";
import MemberBrandDNA from "@/pages/MemberBrandDNA";
import Offer from "@/pages/Offer";
import Services from "@/pages/Services";
import InternationalProduction from "@/pages/InternationalProduction";
import About from "@/pages/About";
import Member from "@/pages/Member";
import Login from "@/pages/Login";
import Welcome from "@/pages/Welcome";
import ResetPassword from "@/pages/ResetPassword";
import MemberBrief from "@/pages/MemberBrief";
import DemoCase from "@/pages/DemoCase";
import OS from "@/pages/OS";
import Portal from "@/pages/Portal";
import DemoMember from "@/pages/DemoMember";
import { captureReferralFromUrl } from "@/lib/referrals";

const TOAST_OPTIONS = {
    classNames: {
        toast: "bg-cafe-cream border-border text-cafe-espresso",
        description: "text-cafe-mocha",
    },
};

function ScrollToTop() {
    const { pathname, hash } = useLocation();
    useEffect(() => {
        if (hash) return;
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }, [pathname, hash]);
    return null;
}

const wrap = (lang, El) => (
    <LanguageProvider lang={lang}><El /></LanguageProvider>
);
const intent = (slug, lang) => (
    <LanguageProvider lang={lang}><IntentPage slug={slug} lang={lang} /></LanguageProvider>
);

const MemberKit = () => <Kit memberMode />;

function App() {
    // Capture ?ref=XXX once on first app load → localStorage for 30 days.
    useEffect(() => { captureReferralFromUrl(); }, []);

    return (
        <div className="App">
            <HelmetProvider>
            <BrowserRouter>
                <ScrollToTop />
                <SeoManager />
                <Routes>
                    <Route path="/" element={wrap("en", Landing)} />
                    <Route path="/cn" element={wrap("cn", Landing)} />
                    <Route path="/work" element={wrap("en", WorkList)} />
                    <Route path="/work/cn" element={wrap("cn", WorkList)} />
                    <Route path="/work/:slug" element={wrap("en", WorkDetail)} />
                    <Route path="/about" element={wrap("en", About)} />
                    <Route path="/about/cn" element={wrap("cn", About)} />
                    <Route path="/journal" element={wrap("en", Journal)} />
                    <Route path="/journal/cn" element={wrap("cn", Journal)} />
                    <Route path="/journal/:slug" element={wrap("en", JournalDetail)} />
                    <Route path="/studio" element={wrap("en", Studio)} />
                    <Route path="/studio/preview/:slug" element={<CaseStudyDraftPreview />} />
                    <Route path="/studio/landing-pages" element={<Navigate to="/studio?tab=clients&sub=landing-pages" replace />} />
                    <Route path="/client/landing-login" element={<ClientLogin />} />
                    <Route path="/client/landing-onboarding" element={<ClientOnboarding />} />
                    <Route path="/client/landing-dashboard" element={<ClientDashboard />} />
                    <Route path="/client/landing-pages/:pageId/edit" element={<ClientEditor />} />
                    <Route path="/client/landing-pages/:pageId/preview" element={<ClientPreview />} />
                    <Route path="/client/landing-pages/:pageId/domain" element={<ClientDomain />} />
                    <Route path="/client/account" element={<ClientAccount />} />
                    <Route path="/client/team" element={<ClientTeam />} />
                    <Route path="/studio/orders/:id" element={wrap("en", StudioOrderEditor)} />
                    <Route path="/intake" element={wrap("en", Intake)} />
                    <Route path="/kit/:id" element={wrap("en", Kit)} />
                    <Route path="/pack/:token" element={<SharedPack />} />
                    <Route path="/gallery" element={wrap("en", Gallery)} />
                    <Route path="/b2b" element={wrap("en", B2B)} />
                    <Route path="/b2b/cn" element={wrap("cn", B2B)} />
                    <Route path="/proposal/:token" element={<SalesProposal />} />
                    <Route path="/portal" element={<Portal />} />
                    <Route path="/ig/content/member/demo" element={<DemoMember />} />
                    <Route path="/studio/docs/:leadId/:docType" element={<DocPrint />} />
                    <Route path="/subscribe" element={wrap("en", Subscribe)} />
                    <Route path="/subscribe/cn" element={wrap("cn", Subscribe)} />
                    <Route path="/subscribe/success" element={wrap("en", SubscribeSuccess)} />
                    <Route path="/checkout/kit/success" element={wrap("en", KitSuccess)} />
                    <Route path="/booking/success" element={wrap("en", BookingSuccess)} />
                    <Route path="/pay-balance/success" element={wrap("en", PayBalanceSuccess)} />
                    <Route path="/pay-balance/:orderId" element={wrap("en", PayBalance)} />
                    <Route path="/offer" element={<Navigate to="/ig/content/AI" replace />} />
                    <Route path="/demo/bayou-cafe" element={wrap("en", DemoCase)} />
                    <Route path="/demo/kaily-fnb-studio" element={wrap("en", DemoCase)} />
                    <Route path="/os" element={<OS />} />
                    <Route path="/offer/cn" element={<Navigate to="/ig/content/AI/cn" replace />} />
                    <Route path="/services" element={<Navigate to="/photo-video-services" replace />} />
                    <Route path="/services/cn" element={<Navigate to="/photo-video-services/cn" replace />} />
                    <Route path="/ig/content/AI" element={wrap("en", Offer)} />
                    <Route path="/ig/content/AI/cn" element={wrap("cn", Offer)} />
                    <Route path="/photo-video-services" element={wrap("en", Services)} />
                    <Route path="/international" element={wrap("en", InternationalProduction)} />
                    <Route path="/international/cn" element={wrap("cn", InternationalProduction)} />
                    <Route path="/f-and-b-production-malaysia" element={<Navigate to="/international" replace />} />
                    <Route path="/food-film-production-malaysia" element={<Navigate to="/international" replace />} />
                    <Route path="/hospitality-brand-film-southeast-asia" element={<Navigate to="/international" replace />} />
                    <Route path="/photo-video-services/cn" element={wrap("cn", Services)} />
                    <Route path="/food-videographer-kuala-lumpur" element={intent("food-videographer-kuala-lumpur", "en")} />
                    <Route path="/food-videographer-kuala-lumpur/cn" element={intent("food-videographer-kuala-lumpur", "cn")} />
                    <Route path="/restaurant-opening-campaign-kl" element={intent("restaurant-opening-campaign-kl", "en")} />
                    <Route path="/restaurant-opening-campaign-kl/cn" element={intent("restaurant-opening-campaign-kl", "cn")} />
                    <Route path="/food-product-video-malaysia" element={intent("food-product-video-malaysia", "en")} />
                    <Route path="/food-product-video-malaysia/cn" element={intent("food-product-video-malaysia", "cn")} />
                    <Route path="/menu-photographer-klang-valley" element={intent("menu-photographer-klang-valley", "en")} />
                    <Route path="/menu-photographer-klang-valley/cn" element={intent("menu-photographer-klang-valley", "cn")} />
                    <Route path="/member" element={wrap("en", Member)} />
                    <Route path="/member/cn" element={wrap("cn", Member)} />
                    <Route path="/member/brief" element={wrap("en", MemberBrief)} />
                    <Route path="/member/digests" element={wrap("en", MemberDigests)} />
                    <Route path="/member/digests/:month" element={wrap("en", MemberDigestDetail)} />
                    <Route path="/member/orders" element={wrap("en", MemberOrders)} />
                    <Route path="/member/kit/:id" element={wrap("en", MemberKit)} />
                    <Route path="/member/brand-dna" element={wrap("en", MemberBrandDNA)} />
                    <Route path="/login" element={<Navigate to="/member/login" replace />} />
                    <Route path="/member/login" element={wrap("en", Login)} />
                    <Route path="/welcome" element={wrap("en", Welcome)} />
                    <Route path="/reset-password" element={wrap("en", ResetPassword)} />
                    <Route path="/:slug" element={<PublicLanding />} />
                </Routes>
            </BrowserRouter>
            </HelmetProvider>
            <Toaster
                position="top-center"
                toastOptions={TOAST_OPTIONS}
            />
        </div>
    );
}

export default App;
